/**
 * @file src/app.jsx
 * @description 
 * Master Platform Orchestrator.
 * Encapsulates global providers and manages the top-level routing lifecycle.
 * 
 * Standards:
 * - 2.1: kebab-case file naming.
 * - 4.1: Deterministic Provider Hierarchy.
 * - 13.4: Graceful Suspense (LoadingState Block).
 */

import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Suspense } from 'react';

// Top-level error boundary — catches unhandled render errors across the full tree
import ErrorBoundary from './ErrorBoundary.jsx';

// Context Providers (Service Orchestrators)
import { AuthProvider } from './context/AuthContext.jsx';
import { PermissionsProvider } from './context/PermissionsContext.jsx';
import { HealthProvider } from './context/HealthContext.jsx';
import { FeatureFlagProvider } from './context/FeatureFlagContext.jsx';

// Core structural components
import MetaData from './core/Meta.jsx';
import RouteTitle from './core/RouteTitle.jsx';
import ScrollToTop from './core/ScrollToTop.jsx';
import BrandTheme from './core/BrandTheme.jsx';
import { routes } from './routes/routes.jsx';

// 🏛️ Atomic UI Orchestration
import { LoadingState } from './components/blocks/loading-state.block.jsx';
import PostLoginIntentHandler from './components/blocks/post-login-intent.block.jsx';
import { CookieBanner } from './components/blocks/cookie-banner.block.jsx';
import { GuestPreviewBanner } from './components/blocks/guest-preview-banner.block.jsx';
import TestPersonaReturn from './components/blocks/test-persona-return.block.jsx';

/**
 * 🛰️ APP CONTENT
 * Logic: Consumes context-ready routing configurations.
 * Implements a hydration guard to prevent premature redirects during mesh sync.
 * 
 * Standard 1.5: Functional boundary separating providers from logic.
 */
function AppContent() {
  const currentRoutes = routes();
  
  /**
   * 🛡️ GUARD: ROUTE HYDRATION (Metric 13.4)
   * Logic: If currentRoutes length is <= 2, it means only /login and /register are defined.
   * If the user is trying to access a internal path (like /projects), we must WAIT
   * for the Dictionary Service to provide the full catalog.
   */
  const isAuthPath = window.location.pathname === '/login' || window.location.pathname === '/register';
  
  if (currentRoutes.length <= 2 && !isAuthPath) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-secondary">
        <LoadingState label="Starting up..." />
      </div>
    );
  }

  return (
    <>
      {/* Lives outside <Routes> so it runs on every route transition
          (the auth state can flip on any page — register, login, or a
          deep link after silent refresh). Renders nothing. */}
      <PostLoginIntentHandler />
      {/* Same reason it sits here: it needs the router's location, and it must
          run on every transition. Renders nothing. */}
      <RouteTitle />
      <ScrollToTop />
      <Routes>
        {currentRoutes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={route.element}
          />
        ))}
      </Routes>
    </>
  );
}

/**
 * 🚀 PLATFORM ROOT
 * Logic: Defines the strict hierarchy of state inheritance.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PermissionsProvider>
          <HealthProvider>
            <FeatureFlagProvider>
              {/* 🏷️ Head Metadata Sync */}
              <MetaData />

              {/* 🎨 Brand colours → CSS variables. Defaults live in
                  styles/index.css, so this only overrides once the
                  dictionary answers. */}
              <BrandTheme />

              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Suspense fallback={<LoadingState label="Loading..." />}>
                  <AppContent />
                </Suspense>
                <CookieBanner />
                {/* Root-level: a preview can reach any route, so the way out
                    must exist on all of them. Renders nothing when inactive. */}
                <GuestPreviewBanner />
                {/* Root-level for the same reason: a switched persona can
                    reach any route, so the way back must exist on all of
                    them. Renders nothing unless a return session is stashed. */}
                <TestPersonaReturn />
              </BrowserRouter>
            </FeatureFlagProvider>
          </HealthProvider>
        </PermissionsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
